import { ProductCategoriesApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as ProductCategoriesActions from './product-categories.actions';
import { selectProductCategoriesParams } from './product-categories.selectors';

@Injectable()
export class ProductCategoriesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private categoriesApi = inject(ProductCategoriesApiService);
  private notification = inject(NotificationService);

  loadProductCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoriesActions.loadProductCategories),
      switchMap(({ params }) =>
        this.categoriesApi.findAll(params).pipe(
          map((response) => ProductCategoriesActions.loadProductCategoriesSuccess({ response })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_product_categories_error');
            return of(ProductCategoriesActions.loadProductCategoriesFailure({
              error: error.message || 'Failed to load product categories',
            }));
          }),
        ),
      ),
    ),
  );

  createProductCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoriesActions.createProductCategory),
      switchMap(({ categoryData }) =>
        this.categoriesApi.create(categoryData).pipe(
          map((category) => {
            this.notification.success('admin.notifications.product_category_created');
            return ProductCategoriesActions.createProductCategorySuccess({ category });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_product_category_error');
            return of(ProductCategoriesActions.createProductCategoryFailure({
              error: error.message || 'Failed to create product category',
            }));
          }),
        ),
      ),
    ),
  );

  updateProductCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoriesActions.updateProductCategory),
      switchMap(({ id, categoryData }) =>
        this.categoriesApi.update(id, categoryData).pipe(
          map((category) => {
            this.notification.success('admin.notifications.product_category_updated');
            return ProductCategoriesActions.updateProductCategorySuccess({ category });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_product_category_error');
            return of(ProductCategoriesActions.updateProductCategoryFailure({
              error: error.message || 'Failed to update product category',
            }));
          }),
        ),
      ),
    ),
  );

  deleteProductCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductCategoriesActions.deleteProductCategory),
      switchMap(({ id }) =>
        this.categoriesApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.product_category_deleted');
            return ProductCategoriesActions.deleteProductCategorySuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_product_category_error');
            return of(ProductCategoriesActions.deleteProductCategoryFailure({
              error: error.message || 'Failed to delete product category',
            }));
          }),
        ),
      ),
    ),
  );

  reloadAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProductCategoriesActions.createProductCategorySuccess,
        ProductCategoriesActions.updateProductCategorySuccess,
        ProductCategoriesActions.deleteProductCategorySuccess,
      ),
      withLatestFrom(this.store.select(selectProductCategoriesParams)),
      map(([ , params ]) => ProductCategoriesActions.loadProductCategories({ params })),
    ),
  );
}
